import { useEffect, useRef } from 'react';

/**
 * Document-level keyboard shortcuts for the audio players.
 *
 * Design notes:
 *  - DOCUMENT-level (not element-focused), so e.g. spacebar play/pause works anywhere on the page,
 *    matching every DAW / SoundCloud. Only ONE player is ever mounted at a time (the nav shows a
 *    track OR a stem set), so there's no cross-player conflict.
 *  - GUARDED against text entry: events originating from an input/textarea/select/contenteditable
 *    are ignored, so typing a space in the comment box types a space (never toggles playback).
 *  - GUARDED against focused buttons: a focused <button> handles Space/Enter natively, so we skip
 *    those to avoid double-firing. The waveform/playhead seek control (role="slider") is NOT
 *    skipped — Space isn't one of its keys, and "click the waveform then hit space" should work.
 *  - LATEST-HANDLER via a ref: the keymap's handlers (which close over fresh state) are read live,
 *    so we never re-subscribe the listener on every render.
 *  - `enabled` arms/disarms the whole map (e.g. only once the player's audio is ready).
 *
 * Keymap keys are KeyboardEvent.code values (layout-independent): 'Space', 'ArrowLeft',
 * 'ArrowRight', 'KeyM', etc. A handler returning (or being present) means we preventDefault()
 * so the key's default action (space = page scroll) doesn't also fire.
 */
export type ShortcutMap = Record<string, (e: KeyboardEvent) => void>;

// Keys that are allowed to fire even while a text field is focused. Escape is the obvious one:
// pressing it in the delete-confirm input or the comment box should cancel/clear, not be swallowed.
const ALLOW_IN_INPUTS = new Set(['Escape']);

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (node.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(map: ShortcutMap, enabled: boolean = true): void {
  // Keep the latest map in a ref so the listener always calls current handlers without
  // re-subscribing (handlers close over fresh component state each render).
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      // Ignore modifier combos — these shortcuts are plain single keys, and we must not hijack
      // browser/OS chords (Cmd/Ctrl/Alt).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const handler = mapRef.current[e.code];
      if (!handler) return;
      // Text fields keep their keys — except an allowlisted few (Escape) that should still fire
      // from inside an input (e.g. to cancel a confirm dialog).
      if (isTypingTarget(e.target) && !ALLOW_IN_INPUTS.has(e.code)) return;
      // A focused button handles Space/Enter itself — don't double-fire.
      const node = e.target as HTMLElement | null;
      if (node?.tagName === 'BUTTON' && (e.code === 'Space' || e.code === 'Enter')) return;
      e.preventDefault();
      handler(e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}
